import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Minus, Plus, Printer } from "lucide-react";
import React from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  cancelBox,
  getBox,
  getBoxesConfig,
  printBoxLabel,
  takeFromBox,
  type TakeIn,
} from "@/api/boxes";
import { groupSscc, itemKey } from "@/lib/boxes";
import { QtyInput } from "@/lib/scan-ui/qty-input";
import { ScanBanner } from "@/pages/fbs/ScanBanner";
import { OriginBadge } from "@/pages/boxes/cargo-ui";
import { apiErrorText, LoadState, useGoBack } from "@/lib/scan-ui/screen";

export function BoxPage(): React.ReactElement {
  const { boxId = "" } = useParams();
  const qc = useQueryClient();
  const goBack = useGoBack("/boxes");
  const [take, setTake] = React.useState<Record<string, number>>({});
  // ⚠️ Степперы показываем ПО НАМЕРЕНИЮ, а не всегда.
  //
  // Раньше они стояли на составе постоянно, и после создания коробки портал
  // вёл сюда печатать наклейку — а экран выглядел как «сколько забрать»:
  // человек только что сложил в коробку, и ему предлагали из неё брать.
  //
  // Намерение приезжает из плитки: «Взять из короба» ставит `?take=1`, и
  // степперы включены сразу — за этим и сканировали. «Что в коробе» открывает
  // карточку справкой.
  const [params] = useSearchParams();
  const [taking_, setTakingMode] = React.useState(params.get("take") === "1");
  const [banner, setBanner] = React.useState<
    { kind: "ok" | "error" | "warn"; text: string } | null
  >(null);

  const box = useQuery({
    queryKey: ["box", boxId],
    queryFn: () => getBox(boxId),
    enabled: Boolean(boxId),
  });
  const config = useQuery({ queryKey: ["boxes-config"], queryFn: getBoxesConfig });
  // Принтер или макет не настроены → кнопка гаснет и ПОДПИСЫВАЕТСЯ, а не
  // падает при нажатии: «не работает» без причины отправляет искать поломку
  // не туда.
  const canPrint = Boolean(config.data?.printer_ready);
  // Причину показываем ТЕКСТОМ с сервера: «не работает» без объяснения
  // отправляет искать поломку не туда.
  const printHint = config.data && !config.data.printer_ready
    ? config.data.printer_problem || "Печать недоступна"
    : "";
  const d = box.data;
  const readOnly = d?.status === "empty" || d?.status === "cancelled";

  const refresh = () => void qc.invalidateQueries({ queryKey: ["box", boxId] });

  // Положить взятое в новую коробку — ОПЦИЯ, по умолчанию выключенная: взятое
  // обычно уезжает в заказ товаром, и номер ему не нужен.
  const [intoBox, setIntoBox] = React.useState(false);

  const take_ = useMutation({
    mutationFn: () => {
      const items: TakeIn[] = (d?.items ?? [])
        .filter((i) => (take[itemKey(i)] ?? 0) > 0)
        // Шлём КЛЮЧ строки целиком: на бэке пустая партия означает «строка
        // без партии», а не «любая», и коробка со смешанным составом (тот же
        // товар без партии и с партией) иначе не разбирается.
        .map((i) => ({
          nomenclature_ref: i.nomenclature_ref,
          partia_ref: i.partia_ref,
          characteristic_ref: i.characteristic_ref,
          qty_base: take[itemKey(i)] ?? 0,
        }));
      return takeFromBox(boxId, [{ items }], { intoBox });
    },
    onSuccess: (res) => {
      setTake({});
      setTakingMode(false);
      const kid = res.children[0];
      const left = res.parent.items.length > 0;
      setBanner({
        kind: left ? "warn" : "ok",
        // ⚠️ Про наклейку остатка говорим ВСЕГДА, пока в коробе что-то есть:
        // в её DataMatrix записано прежнее количество, и после изъятия она
        // врёт. Верят коробу именно по коду — на отгрузке и инвентаризации.
        text: kid
          ? `Взято в новую коробку ${groupSscc(kid.sscc)}.` +
            (left ? " Наклейку остатка перепечатайте — старая врёт." : "")
          : left
            ? "Взято. Наклейку перепечатайте — на старой прежнее количество."
            : "Взято всё, короб пуст. Наклейку снимите.",
      });
      refresh();
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });

  const print = useMutation({
    mutationFn: () => printBoxLabel(boxId),
    onSuccess: () => {
      setBanner({ kind: "ok", text: "Наклейка отправлена на принтер." });
      refresh();
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });
  const cancel = useMutation({
    mutationFn: () => cancelBox(boxId),
    onSuccess: () => refresh(),
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });

  /** Клэмп в ОДНОМ месте: и кнопки, и поле ввода не дают взять больше, чем есть. */
  const setPicked = (key: string, value: number, max: number) =>
    setTake((s) => ({ ...s, [key]: Math.min(max, Math.max(0, value)) }));

  const taking = Object.values(take).reduce((s, n) => s + n, 0);

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={goBack} className="text-sm text-slate-500">
          ←
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">Коробка</span>
            <OriginBadge origin="portal" />
          </div>
          <div className="break-all font-mono text-lg">{d ? groupSscc(d.sscc) : ""}</div>
          <div className="text-xs text-slate-500">
            {d?.characteristic_name || "без характеристики"}
            {d?.warehouse_name ? ` · ${d.warehouse_name}` : ""}
          </div>
        </div>
        {readOnly && (
          <span className="rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-600">
            {d?.status === "empty" ? "пуста" : "отменена"}
          </span>
        )}
      </div>

      {banner && (
        <ScanBanner tone={banner.kind} title={banner.text} onClose={() => setBanner(null)} />
      )}

      {(box.isLoading || box.error) && (
        <LoadState
          isLoading={box.isLoading}
          error={box.error}
          onRetry={() => void box.refetch()}
        />
      )}

      {d && (
        <>
          {!readOnly && !d.printed && d.items.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Наклейка не напечатана. Без неё короб на складе не отличить от
              соседнего.
            </div>
          )}

          <div className="rounded-lg border bg-white">
            <div className="border-b px-3 py-2 text-sm font-medium">Внутри</div>
            {d.items.length === 0 && (
              <div className="p-4 text-sm text-slate-500">
                Пусто — из этой коробки всё вынули.
              </div>
            )}
            {d.items.map((i) => {
              const key = itemKey(i);
              const picked = take[key] ?? 0;
              const max = Number(i.qty_base || 0);
              return (
                /* ⚠️ Наименование ЦЕЛИКОМ, без `truncate`, и степпер под ним,
                   а не рядом. На телефоне и ТСД строка «Гель для душа "La
                   Fabrique" с ароматом…» обрезалась многоточием ровно там, где
                   начинается отличие одной позиции от другой, — а выбирать,
                   сколько вынуть, надо именно между ними. Тот же довод, по
                   которому в проверке заказов позиции ушли в шторку с полным
                   наименованием. */
                <div key={key} className="border-b px-3 py-2 last:border-0">
                  <div className="min-w-0">
                    <div className="break-words text-sm">
                      {i.nomenclature_name || i.nomenclature_article || i.nomenclature_ref}
                    </div>
                    <div className="text-xs text-slate-500">
                      {i.partia_name ? `партия ${i.partia_name}` : "без партии"}
                      {i.characteristic_name ? ` · ${i.characteristic_name}` : ""} · {max} шт
                    </div>
                  </div>
                  {!readOnly && taking_ && (
                    <div className="mt-2 flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={picked <= 0}
                        onClick={() => setPicked(key, picked - 1, max)}
                      >
                        <Minus className="size-4" />
                      </Button>
                      {/* ⚠️ Поле ввода, а не только «+»: отложить 150 штук
                          тапами — 150 нажатий. Урок уже записан в
                          `scan-ui/qty-input.tsx` («14000 флаконов вносили
                          нажатиями “+”»), и здесь тот же случай. */}
                      <QtyInput
                        value={picked}
                        onChange={(v) => setPicked(key, v, max)}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={picked >= max}
                        onClick={() => setPicked(key, picked + 1, max)}
                      >
                        <Plus className="size-4" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!readOnly && taking_ && taking > 0 && (
            <label className="flex items-start gap-2 rounded-lg border bg-white p-3 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 size-4"
                checked={intoBox}
                onChange={(e) => setIntoBox(e.target.checked)}
              />
              <span>
                Положить взятое в новую коробку
                <span className="block text-xs text-slate-500">
                  Нужно, только если взятое едет отдельной тарой. Обычно оно
                  уходит в заказ товаром — тогда номер ему не нужен.
                </span>
              </span>
            </label>
          )}

          {/* ⚠️ Кнопки в СТОЛБЕЦ на узком экране: в ряд они не помещались, и
              правая уезжала за край — на боевом телефоне её не было видно.

              ⚠️ Кнопки «Взять из коробки» здесь НЕТ. Взятие начинается со
              скана в разделе «Взять из короба» и приезжает сюда как `?take=1`.
              Из короба не берут многократно подряд: взяли, напечатали наклейку
              остатка и ушли — понадобится ещё, отсканируют его заново. Кнопка
              же предлагала брать из коробки, которую человек только что
              собрал. */}
          {!readOnly && (
            <div className="flex flex-col gap-2 sm:flex-row">
              {/* Печать — главное действие: портал ведёт сюда сразу после
                  создания коробки и после изъятия, и в оба момента нужно одно
                  — наклеить актуальный номер и количество. */}
              <Button
                className="sm:flex-1"
                variant={d.printed ? "outline" : "default"}
                disabled={print.isPending || d.items.length === 0 || !canPrint}
                onClick={() => print.mutate()}
              >
                <Printer className="mr-1 size-4" />
                {d.printed ? "Перепечатать наклейку" : "Напечатать наклейку"}
              </Button>

              {taking_ && (
                <Button
                  className="sm:flex-1"
                  disabled={taking <= 0 || take_.isPending}
                  onClick={() => take_.mutate()}
                >
                  Взять {taking > 0 ? `${taking} шт` : ""}
                  {intoBox ? " в новую коробку" : ""}
                </Button>
              )}
            </div>
          )}

          {/* ⚠️ Причина, по которой печать недоступна, — ОТДЕЛЬНОЙ строкой, а
              не текстом внутри кнопки. Сообщение про ненастроенный принтер
              длинное, и кнопка растягивалась на весь экран, оставаясь при этом
              неактивной: выглядело так, будто это она и есть. */}
          {!readOnly && printHint && (
            <p className="text-xs text-slate-500">{printHint}</p>
          )}

          {(d.came_from.length > 0 || d.went_to.length > 0) && (
            <div className="rounded-lg border bg-white p-3 text-xs text-slate-600">
              {d.came_from.length > 0 && (
                <div>Пришла из: {d.came_from.map((b) => groupSscc(b.sscc)).join(", ")}</div>
              )}
              {d.went_to.length > 0 && (
                <div className="mt-1">
                  Из неё сделали: {d.went_to.map((b) => groupSscc(b.sscc)).join(", ")}
                </div>
              )}
            </div>
          )}

          {!readOnly && d.items.length > 0 && (
            <button
              className="text-xs text-rose-600 underline"
              onClick={() => cancel.mutate()}
            >
              Коробки не существует — отменить
            </button>
          )}
        </>
      )}
    </div>
  );
}
